import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FolderOpen, ExternalLink, FileText, Search, Bookmark, BookmarkCheck } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { SectionLoading } from '@/components/community/ui'
import {
  listResources, saveResource, unsaveResource, getSavedResources, type Resource,
} from '@/services/resourceService'

export default function CommunityResourcesPage() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const [items, setItems] = useState<Resource[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [topic, setTopic] = useState('')
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set())
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    // Load the published VOLUNTEER-audience library + the volunteer's saved ids
    // (so each card shows the correct saved state in one pass).
    Promise.allSettled([
      listResources({ audience: 'VOLUNTEER', q: query || undefined, topic: topic || undefined, size: 60 }),
      getSavedResources(0, 60),
    ]).then(([res, sav]) => {
      if (res.status === 'fulfilled') setItems(res.value.content)
      if (sav.status === 'fulfilled') setSavedIds(new Set(sav.value.content.map((s) => s.id)))
      setLoading(false)
    })
  }, [query, topic])

  useEffect(() => { load() }, [load])

  const onToggleSave = async (r: Resource) => {
    setBusyId(r.id)
    const wasSaved = savedIds.has(r.id)
    // Optimistic toggle
    setSavedIds((p) => { const n = new Set(p); wasSaved ? n.delete(r.id) : n.add(r.id); return n })
    try {
      if (wasSaved) await unsaveResource(r.id); else await saveResource(r.id)
    } catch {
      // Revert on failure
      setSavedIds((p) => { const n = new Set(p); wasSaved ? n.add(r.id) : n.delete(r.id); return n })
    } finally { setBusyId(null) }
  }

  if (!community) return null

  const href = (r: Resource) => r.externalUrl || r.fileUrl || '#'

  return (
    <CommunityLayout>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <GovPageTitle title={t('community.resources.title')} subtitle={t('community.resources.subtitle')} icon={FolderOpen} />
        <Link to="/community-dashboard/saved-resources" className="mt-1 text-sm font-medium text-blue-600 hover:underline inline-flex items-center gap-1 min-h-[40px]">
          <Bookmark className="w-4 h-4" aria-hidden="true" />{t('community.savedResources.title')}
        </Link>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('community.resources.search')}
            className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none min-h-[44px]" />
        </div>
        <select value={topic} onChange={(e) => setTopic(e.target.value)} aria-label={t('community.resources.topic')}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 outline-none min-h-[44px]">
          <option value="">{t('community.resources.allTopics')}</option>
          {['SYMPTOMS', 'DIAGNOSIS', 'TREATMENT', 'NUTRITION', 'PREVENTION', 'AWARENESS', 'CAMPAIGN', 'NIKSHAY_MITRA'].map((tp) => (
            <option key={tp} value={tp}>{t(`community.resources.topic_${tp}`, tp)}</option>
          ))}
        </select>
      </div>

      {loading ? <SectionLoading rows={4} /> : items.length === 0 ? (
        <GovEmptyState icon="📁" title={t('community.resources.none')} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((r) => {
            const saved = savedIds.has(r.id)
            return (
              <GovCard key={r.id}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-blue-600" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 line-clamp-2">{r.title}</h3>
                    {r.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{r.description}</p>}
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-400 flex-wrap">
                      <span>{t(`community.resources.type_${r.resourceType}`, r.resourceType)}</span>
                      {r.sourceOrg && <span>· {r.sourceOrg}</span>}
                      {r.language && <span>· {r.language}</span>}
                    </div>
                    <div className="mt-2 flex items-center gap-4">
                      <a href={href(r)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline min-h-[40px]">
                        <ExternalLink className="w-4 h-4" aria-hidden="true" />{t('community.resources.open')}
                      </a>
                      <button onClick={() => onToggleSave(r)} disabled={busyId === r.id}
                        aria-pressed={saved}
                        className={`inline-flex items-center gap-1 text-sm font-medium hover:underline disabled:opacity-50 min-h-[40px] ${saved ? 'text-blue-600' : 'text-gray-600'}`}>
                        {saved ? <BookmarkCheck className="w-4 h-4" aria-hidden="true" /> : <Bookmark className="w-4 h-4" aria-hidden="true" />}
                        {saved ? t('community.resources.saved') : t('community.resources.save')}
                      </button>
                    </div>
                  </div>
                </div>
              </GovCard>
            )
          })}
        </div>
      )}
    </CommunityLayout>
  )
}